import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { isEmpty } from 'validate.js';

import {
  FILTER_POSTAL_CODE,
  FILTER_DISTANCE_IN_MILES,
  FILTER_LATITUDE,
  FILTER_LONGITUDE,
  FILTER_LOCATION_NAME,
  PAGE,
  LOCATION_REDIRECT,
  SORT
} from 'dpl/constants/query_params';
import { pushParams, resetParams } from 'dpl/actions/query_params';
import { addToQuery, removeFromQuery } from 'dpl/util/queryString';
import {
  RESULTS_TYPES,
  SORT_NEAR_YOU
} from 'dpl/puppy_search_results/utils/constants';
import { isMobileUA } from 'dpl/shared/utils';
import LocationAutocompleteContainer from 'dpl/search/containers/LocationAutocompleteContainer';
import { isLessThanBreakpoint } from 'dpl/util/grid';

import MobileFilterButtons from './MobileFilterButtons';

const DEFAULT_DISTANCE = '0';
const IS_LT_MD = isLessThanBreakpoint('md');

export default function LocationFilter({
  title,
  inputClassName,
  resultsType,
  isOpen,
  filterButtonProps
}) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const { preappliedFilters, rootPath } = useSelector(({ server }) => server);
  const [locationResults, setLocationResults] = useState([]);

  const selectedLocation = useSelector(({ queryParams }) => {
    if (queryParams[FILTER_POSTAL_CODE]) {
      return queryParams[FILTER_POSTAL_CODE];
    }

    if (queryParams[FILTER_LOCATION_NAME]) {
      return decodeURIComponent(queryParams[FILTER_LOCATION_NAME]);
    }

    return '';
  });

  const [searchValue, setSearchValue] = useState(selectedLocation);

  useEffect(() => {
    setSearchValue(selectedLocation);
  }, [selectedLocation]);

  useEffect(() => {
    if (!isMobileUA() && isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  async function handleLocationSelect(location) {
    const locationName = location.name.replace(/, USA$/, '');

    const params = {
      [FILTER_LOCATION_NAME]: locationName,
      // We're only passing along distance_in_miles for analytics purposes
      // since postal codes are appended automatically when ip_location_filter
      // so this is the only reliable way to know if location filtering is being done
      [FILTER_DISTANCE_IN_MILES]: DEFAULT_DISTANCE,
      ...(resultsType === RESULTS_TYPES.BREEDERS && { [SORT]: SORT_NEAR_YOU })
    };

    if (preappliedFilters?.stateSlug) {
      const newSearchStr = removeFromQuery(PAGE, window.location.search);

      window.location.href = `${rootPath}/?${LOCATION_REDIRECT}=true&${newSearchStr}#${addToQuery(
        params,
        window.location.hash
      )}`;
    } else {
      dispatch(
        resetParams(
          PAGE,
          FILTER_LATITUDE,
          FILTER_LONGITUDE,
          FILTER_LOCATION_NAME,
          FILTER_POSTAL_CODE
        )
      );
      dispatch(resetParams(FILTER_POSTAL_CODE, { hash: true }));
      dispatch(pushParams(params, { hash: true }));
    }

    // Close bottom sheet or dropdown after selection
    filterButtonProps?.setIsMenuOpen?.(false);
    inputRef.current?.blur?.();
  }

  function handleFirstLocationSelect() {
    if (isEmpty(locationResults)) return;

    // When the filter button is clicked without selecting a location,
    // use the first location available (which is the closest match,
    // based on the input query)
    handleLocationSelect(locationResults[0]);
  }

  return (
    <>
      {title && (
        <p className="mb2 font-16 fw-medium md:font-18 md:font-serif">
          {title}
        </p>
      )}
      <p className="font-14 md:font-16 secondary mb6-md mb2">
        We&apos;ll show you puppies from breeders near you.
      </p>
      <p className="mb2 font-12 secondary">Location</p>
      <LocationAutocompleteContainer
        menuClassName="LocationFilter__resultsList overflow-y-auto"
        menuPosition="static"
        menuCloseTimeout={IS_LT_MD ? 0 : 300}
        onSelect={handleLocationSelect}
        query={searchValue}
        onResultsChange={setLocationResults}
        inputProps={{
          name: 'location',
          type: 'text',
          ref: inputRef,
          className: classnames(
            'ba b--light-gray br3 pv2 ph3 mb2',
            inputClassName
          ),
          onChange: e => setSearchValue(e.target.value),
          placeholder: 'Enter a city or zipcode'
        }}
      />
      {filterButtonProps && (
        <MobileFilterButtons
          {...filterButtonProps}
          className="mt12 mt8-md"
          onClick={handleFirstLocationSelect}
        />
      )}
    </>
  );
}

LocationFilter.propTypes = {
  title: PropTypes.string,
  inputClassName: PropTypes.string,
  resultsType: PropTypes.oneOf(Object.values(RESULTS_TYPES)),
  isOpen: PropTypes.bool,
  filterButtonProps: PropTypes.shape({
    resultsType: PropTypes.oneOf(Object.values(RESULTS_TYPES)).isRequired,
    totalItemCount: PropTypes.number.isRequired,
    setIsMenuOpen: PropTypes.func.isRequired
  })
};

LocationFilter.defaultProps = {
  title: null,
  inputClassName: 'w-100 w-50-md',
  resultsType: RESULTS_TYPES.PUPPIES,
  isOpen: false,
  filterButtonProps: null
};
